import type { ReactNode } from 'react';

import { stateIllustrations } from '../../../../assets/src/illustrations';
import { STATE_PANEL_PRESET_TITLES } from './TovStatePanelCatalog';
import { TovIcon } from '../TovIcon/TovIcon';

export type StatePanelIllustration =
  | 'empty'
  | 'error'
  | 'not-found'
  | 'no-results'
  | 'unknown-direction'
  | 'billing-required';

export type StatePanelPreset = keyof typeof STATE_PANEL_PRESET_TITLES;

const presetMeta: Record<
  StatePanelPreset,
  {
    illustration: StatePanelIllustration | null;
    tone: 'default' | 'critical';
  }
> = {
  loading: {
    illustration: null,
    tone: 'default',
  },
  'occasion-not-available': {
    illustration: 'unknown-direction',
    tone: 'critical',
  },
  'feature-unavailable': {
    illustration: 'empty',
    tone: 'critical',
  },
  'access-unavailable': {
    illustration: 'unknown-direction',
    tone: 'critical',
  },
  'unknown-error': {
    illustration: 'error',
    tone: 'critical',
  },
  'not-found': {
    illustration: 'not-found',
    tone: 'default',
  },
  empty: {
    illustration: 'empty',
    tone: 'default',
  },
  'no-results': {
    illustration: 'no-results',
    tone: 'default',
  },
};

export interface StatePanelProps {
  preset: StatePanelPreset;
  detail?: string | null;
  actions?: ReactNode;
  illustration?: StatePanelIllustration;
  tone?: 'default' | 'critical';
}

function getIllustrationAsset(illustration: StatePanelIllustration) {
  switch (illustration) {
    case 'empty':
      return stateIllustrations.emptyBox;
    case 'error':
      return stateIllustrations.errorBrokenConnection;
    case 'not-found':
      return stateIllustrations['404'];
    case 'no-results':
      return stateIllustrations.noResultsSearch;
    case 'unknown-direction':
      return stateIllustrations.unknownDirection;
    case 'billing-required':
      return stateIllustrations.emptyShoppingBags;
  }
}

function resolveTitle(preset: StatePanelPreset) {
  return STATE_PANEL_PRESET_TITLES[preset]();
}

export function StatePanel({
  preset,
  detail,
  actions,
  illustration,
  tone,
}: StatePanelProps) {
  const title = resolveTitle(preset);
  const resolvedTone = tone ?? presetMeta[preset].tone;
  const resolvedIllustration = illustration ?? presetMeta[preset].illustration;
  const illustrationAsset = resolvedIllustration
    ? getIllustrationAsset(resolvedIllustration)
    : null;

  if (preset === 'loading') {
    return (
      <section
        aria-busy="true"
        aria-live="polite"
        aria-label={title}
        className="state-panel state-panel--loading"
      >
        <TovIcon
          className="state-panel__spinner tov-icon--spin"
          name="arrows-clockwise"
        />
        <span className="state-panel__sr-only">
          {title}
          {detail ? ` ${detail}` : ''}
        </span>
      </section>
    );
  }

  return (
    <section className={`state-panel state-panel--${resolvedTone}`}>
      {illustrationAsset ? (
        <img
          alt=""
          aria-hidden="true"
          className="state-panel__illustration"
          src={illustrationAsset}
        />
      ) : null}
      <h1>{title}</h1>
      {detail ? <p>{detail}</p> : null}
      {actions ? <div className="state-panel__actions">{actions}</div> : null}
    </section>
  );
}
